<template>
  <div class="publication">
    <div class="updating" v-show="updating">
      <i class="fa-light fa-circle-notch fa-spin"></i>
    </div>
    <div class="content" :class="{ 'loading-overlay': updating }">
      <div class="header">
        <div class="title-wrapper">
          <p class="publication-name">{{ publication.name }}</p>
          <span>
            <Infobox right :width="status.width" class="info-box" slot="info">
              <span slot="icon" v-switch="status.state">
                <span v-case="'ok'"><i class="fa-light fa-circle-check text-success"></i></span>
                <span v-case="'warning'"><i class="fa-light fa-circle-exclamation text-warning"></i></span>
                <span v-case="'danger'"><i class="fa-light fa-circle-exclamation text-danger"></i></span>
              </span>
              <p class="text-small description" v-html="status.infoMessage"></p>
              <ul class="issue-list">
                <li class="text-extra-strong" v-for="(issue, index) in status.list" :key="index">{{ issue }}</li>
              </ul>
            </Infobox>
          </span>
        </div>
        <div class="publication-actions">
          <button
            class="btn btn-icon-only"
            @click="onRepublish(publication)"
            :tooltip="$t('republish')"
            tooltip-placement="bottom"
            :class="{'btn-notification' : changes}"
          >
            <span> <i class="fa-light fa-file-export"></i></span>
          </button>
          <button
            class="btn btn-icon-only"
            @click="showStats(publication)"
            :tooltip="'Stats'"
            tooltip-placement="bottom"
          >
            <span> <i class="fa-light fa-chart-pie"></i></span>
          </button>
          <button
            class="btn btn-icon-only"
            @click="$emit('start-export', publication)"
            :tooltip="$t('template.export-data')"
            tooltip-placement="bottom"
            v-show="publication.sources.length && exportableSources.includes(publication.sources[0].id)"
          >
            <span> <i class="fa-light fa-download"></i></span>
          </button>
          <button
            class="btn btn-icon-only"
            @click="$emit('start-push', publication)"
            :tooltip="$t('template.push-data')"
            tooltip-placement="bottom"
            v-show="publication.sources.length && triggeredSources.includes(publication.sources[0].id)"
          >
            <span> <i class="fa-light fa-upload"></i></span>
          </button>
          <button
            class="btn btn-icon-only"
            @click="generateUrl(publication)"
            :tooltip="fixedConnectorValues.includes(publication.contentType.name) ? 'Webhook' : 'Endpoint'"
            tooltip-placement="bottom"
          >
            <span> <i class="fa-light fa-webhook"></i></span>
          </button>
          <button
            class="btn btn-icon-only"
            @click="onDeletePublication(publication)"
            :tooltip="$t('template.delete-pub')"
            tooltip-placement="bottom"
          >
            <span> <i class="fa-light fa-trash"></i></span>
          </button>
        </div>
      </div>

      <div class="publication-metadata-content">
        <div class="description-wrapper">
          <p class="content-description">Info</p>
          <p v-if="templateChange" class="change-warning">
            <span>
              <i class="fa-light fa-circle-exclamation"></i>
            </span>
            <span>{{ $t('template.tp-panel-update-message-template') }}</span>
          </p>
        </div>
        <div class=" publication-info">
          <span>
            {{ $t("template.tp-panel-single-info-id") }}
          </span>
          <span class="info-value">
            {{ publication.id }}
          </span>
        </div>

        <div class=" publication-info">
          <span>
            {{ $t("template.tp-panel-single-info-created") }}
          </span>
          <span class="info-value">
            {{ new Date(publication.created) | dateFormat("DD.MM.YYYY kl. HH:mm") }}
          </span>
        </div>

        <div class=" publication-info">
          <span>
            {{ $t("template.tp-panel-single-info-publish") }}
          </span>
          <span class="info-value">
            {{ new Date(publication.lastEdit) | dateFormat("DD.MM.YYYY kl. HH:mm") }}
          </span>
        </div>

        <div class=" publication-info">
          <span>
            {{ $t("template.tp-panel-single-info-publish-by") }}
          </span>
          <span class="info-value">
            {{ publication.lastEditBy ? publication.lastEditBy.name : $store.state.user.name }}
          </span>
        </div>
        <!-- <div class=" publication-info">
          <span>
            Template updated:
          </span>
          <span class="info-value">
            {{ new Date(template.lastEdit) | dateFormat("DD.MM.YYYY kl. HH:mm") }}
          </span>
        </div> -->
      </div>

      <div class="template-settings-content" v-if="filteredPublicationFields.length">
        <div class="description-wrapper">
          <p class="content-description"><span>{{ $t('template.tp-panel-settings') }}</span></p>
          <p v-show="settingsChange" class="change-warning">
            <span>
              <i class="fa-light fa-circle-exclamation"></i>
            </span>
            <span>{{ $t('template.tp-panel-update-message-settings') }}</span>
          </p>
        </div>
        <div v-for="(field, index) in filteredPublicationFields" :key="index">
          <div class="publication-info" v-if="publication.settings && publication.settings[field.id]">
            <span v-html="`${field.name}:`"></span>
            <span class="text-extra-strong">
              {{ publication.settings[field.id] }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Infobox from "@/components/Infobox";
import ConfirmModal from "@/components/modals/ConfirmModal";
import PublicationStats from "@/components/modals/PublicationStats";
import GenerateWebhook from "@/components/modals/GenerateWebhook";
import appHelper from "@/assets/js/appHelper.js";
import { vSwitch, vCase, vDefault } from "v-switch-case";
export default {
  directives: {
    switch: vSwitch,
    case: vCase,
    default: vDefault
  },
  props: {
    publication: Object,
    exportableSources: Array,
    triggeredSources: Array,
    template: Object
  },
  data() {
    return {
      updating: false,
      publicationFields: {},
      templateSettingsCopy: null,
      publicationFieldBlacklist: ["exampleId", "variantOptionTags"]
    };
  },
  computed: {
    filteredPublicationFields() {
      return this.publicationFields.length
        ? this.publicationFields.filter((f) => !this.publicationFieldBlacklist.includes(f.id))
        : [];
    },
    templateChange() {
      const templateUpdated = new Date(this.template.lastEdit);
      const lastPublished = new Date(this.publication.lastEdit);
      return templateUpdated.getTime() > lastPublished.getTime();
    },
    templateSettings() {
      let settings = Object.assign({}, this.template.settings);

      this.publicationFieldBlacklist.forEach((e) => {
        settings = appHelper.removeObjectKey(settings, e);
      });

      return appHelper.stringifyObjectValues(settings);
    },
    publicationSettings() {
      let settings = Object.assign({}, this.publication.settings);

      this.publicationFieldBlacklist.forEach((e) => {
        settings = appHelper.removeObjectKey(settings, e);
      });

      return appHelper.stringifyObjectValues(settings);
    },
    settingsChange() {
      const templateKeys = Object.keys(this.templateSettings);
      const publicationKeys = Object.keys(this.publicationSettings);

      if (templateKeys.length !== publicationKeys.length) {
        return true;
      }
      for (const key of templateKeys) {
        if (this.templateSettings[key] != this.publicationSettings[key]) {
          return true;
        }
      }
      return false;
    },
    changes() {
      return this.templateChange || this.settingsChange;
    },
    status() {
      let list = [];
      if (this.changes) {
        if (this.templateChange) list.push(this.$t("template.tp-panel-status-content"));
        if (this.settingsChange) list.push(this.$t("template.tp-panel-status-settings"));
        return {
          infoMessage: this.$t("template.tp-panel-status-info-msg"),
          state: "warning",
          list: list,
          width: 250
        };
      } else return { infoMessage: "Ok", state: "ok", width: 40 };
    },
    fixedConnectorValues() {
      return this.$store.state.fixedConnectors.map((c) => c.value);
    }
  },
  methods: {
    onRepublish(publication) {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t("republish"),
          description: `${this.$t("template.republish-msg")} <strong>${publication.name}<strong> `,
          confirmButtonText: this.$t("republish"),
          onConfirm: () => {
            this.updating = true;
            this.$store
              .dispatch("publishTemplate", { template: this.template, existingPublication: publication })
              .then((res) => {
                //add some updated bool
                this.updating = false;
              })
              .catch((e) => {
                //add some error handling
                this.updating = false;
              });
          }
        },
        { width: 350, height: "auto" }
      );
    },
    showStats(publication) {
      this.$modal.show(
        PublicationStats,
        {
          publication: publication,
          template: this.template
        },
        { width: 310, height: "auto" }
      );
    },
    generateUrl(publication) {
      this.$modal.show(
        GenerateWebhook,
        {
          publication: publication
        },
        { width: "90%", maxWidth: 600, height: "auto", adaptive: true }
      );
    },
    onDeletePublication(template) {
      this.$store.dispatch("deleteTemplate", template).then(() => {
        this.$emit("success");
      });
    },
    getPublicationFields() {
      this.$store.dispatch("getTemplateSettingsFields", this.publication.id).then((res) => {
        this.publicationFields = res;
      });
    }
  },
  mounted() {
    this.getPublicationFields();
    // this.templateSettingsCopy = Object.assign({}, this.template.settings);
  },
  components: {
    Infobox
  }
};
</script>

<style lang="less">
.publication {
  display: flex;
  align-items: flex-start;
  margin-top: 15px;
  border: 1px solid var(--charcoal-20);
  border-radius: var(--standard-border-radius);
  border-radius: 8px;
  position: relative;
  .updating {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .content {
    cursor: pointer;
    flex-grow: 1;
    .publication-name {
      font-weight: 500;
      margin-bottom: 0px;
      svg {
        margin-right: 15px;
        font-size: 16px;
      }
    }
    .publication-info {
      display: flex;
      gap: 12px;
      // justify-content: space-between;
      font-size: 12px;
      color: var(--charcoal-75);
      .info-value {
        font-weight: 500;
      }
    }
    .publication-metadata-content,
    .template-settings-content {
      padding: 8px 18px 12px 18px;

      .content-description {
        color: var(--charcoal-75);
        font-weight: 500;
        margin-bottom: 4px;
      }
    }
    .template-settings-content {
      border-top: 1px solid var(--charcoal-20);
    }
  }

  .title-wrapper {
    // margin-bottom: 8px;
    gap: 12px;
    align-items: center;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--charcoal-20);
    padding: 6px 18px;
  }
  .description-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 6px;
  }
  .change-warning {
    color: var(--orange-web);
    font-size: 12px;
    margin-bottom: 0;
    // display: flex;
    // gap: 8x;
    // align-items: center;
    > span{
      margin-left: 6px;
    }
  }
  .publication-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;

    .dropdown-item {
      gap: 12px;
      display: flex;
      justify-content: space-between;
    }
    .webhook img {
      width: 16px;
    }
  }
  .info-box {
    color: var(--charcoal);
    p {
      margin: 0;
    }
  }
  .issue-list {
    color: var(--charcoal);
    list-style-type: disc !important;
  }
  .description {
    margin: 0;
  }
}
</style>
